import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import {
  PasswordInputOnly,
  PasswordValidationAndSubmit,
} from '@features/onboarding/components/shared/PasswordInputWithValidation';
import { AuthLayout } from '@features/auth/layouts/AuthLayout';
import { useResetPassword } from '@features/auth/lib/useResetPassword';

import { FormField, FormItem, FormControl } from '@components/ui/form';
import { Form } from '@components/ui/form';
import { FloatingInput } from '@components/ui/input-floating';

const ResetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters long.')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter.')
      .regex(
        /[0-9]|[^A-Za-z0-9\s]/,
        'Password must contain at least one number or special character.'
      ),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match.",
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof ResetPasswordSchema>;

export const ResetPasswordForm: React.FC = () => {
  const { resetPassword, error, isLoading, success, clear } = useResetPassword();
  const [ disabledSubmit, setDisabledSubmit ] = useState(false);
  const navigate = useNavigate();

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    // mode: 'onChange',
  });

  const password = form.watch('password');

  const {
    formState: { errors },
    clearErrors,
  } = form;

  useEffect(() => {
    setDisabledSubmit(!!Object.values(errors).length);
  }, [JSON.stringify(errors)]);

  useEffect(() => {
    if (!success) return;

    const timeoutId = window.setTimeout(() => {
      navigate('/login', { replace: true });
    }, 2000);

    return () => window.clearTimeout(timeoutId);
  }, [success, navigate]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    await resetPassword(data);
  };

  return (
    <AuthLayout
      title="Create a new password."
      variant="new-password"
      titleClassName="font-extrabold text-[40px] leading-tight max-md:text-[28px]"
      headerClassName="2xl:pt-20 2xl:px-6 max-md:pb-72"
      contentClassName="2xl:pt-48 2xl:pl-24"
    >
      <div className="w-full max-w-[450px]">
        <section className="mb-3">
          <h2 className="text-4xl font-extrabold leading-8 mb-2">
            Enter a new password.
          </h2>
          <p className="text-[14px] max-sm:max-w-87 flex-wrap max-w-[430px]">
            Enter a new password and then enter it again to confirm that it's
            correct.
          </p>
        </section>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="max-w-[430px]"
          >
            <PasswordInputOnly form={form} label="Enter a new password" />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <FloatingInput
                      id="confirmPassword"
                      label="Confirm your password"
                      type="password"
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                        clearErrors('confirmPassword');
                        clear();
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      inputClass="h-16 rounded-lg mt-5 border border-[#B7B7B7] p-2 w-full max-w-107.5 max-sm:max-w-full disabled:text-[#ffffff55] disabled:opacity-100 "
                    />
                  </FormControl>
                  {fieldState.error && (
                    <p className="text-primary text-sm mt-1">
                      {fieldState.error.message}
                    </p>
                  )}
                </FormItem>
              )}
            />
            <PasswordValidationAndSubmit
              form={form}
              onSubmit={form.handleSubmit(onSubmit)}
              buttonText={isLoading ? 'Submitting...' : 'SUBMIT'}
              isSubmitDisabled={disabledSubmit || isLoading}
            >
              {error && <p className="text-primary text-sm mt-4 -mb-4">{error}</p>}
              {success && <p className="text-green-700 text-sm font-medium mt-4 -mb-4">{success}</p>}
            </PasswordValidationAndSubmit>

            <p className="text-base xs:text-xl font-extrabold mt-10 text-center lg:text-left w-full max-w-[430px]">
              Returning user?{' '}
              <Link
                to="/login"
                className="text-primary hover:underline whitespace-nowrap"
              >
                Click here to log in.
              </Link>
            </p>
          </form>
        </Form>
      </div>
    </AuthLayout>
  );
};
